import React, { useCallback, useMemo } from 'react';
import { Typography, Tooltip } from '@mui/material';
import { TimesheetLabelValue, LabelValue } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { SearchableChip } from 'modules/common/components/Chip';
import { useChipState } from 'modules/common/hooks/useChipState';
import useActivity from './useActivity';

type ActivityChipProps = {
  label: TimesheetLabel;
  onSelect: (value: LabelValue | null) => void;
};

const getOptionSelected = (
  option: TimesheetLabelValue,
  selected: TimesheetLabelValue
) => option.id === selected?.id;

export const ActivityChip: React.FC<ActivityChipProps> = ({
  label,
  onSelect
}: ActivityChipProps) => {
  const { options, handleSearch, hasMore, loading } = useActivity();

  const onOpen = useCallback(() => {
    handleSearch('');
  }, [handleSearch]);

  const optionLabel = useCallback(
    (option: TimesheetLabelValue) => option.name || '',
    []
  );

  const onSelectValue = useCallback(
    (value: TimesheetLabelValue | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.name
      };
      onSelect(transformedValue);
    },
    [onSelect]
  );

  const acceptSuggestionHandler = useCallback(
    (value: LabelValue) => onSelect(value),
    [onSelect]
  );
  const defaultRenderOption = useCallback((option: TimesheetLabelValue) => {
    return (
      <Tooltip followCursor={true} title={option.name || ''} disableInteractive>
        <Typography variant="inherit" noWrap>
          {option.name}
        </Typography>
      </Tooltip>
    );
  }, []);

  const { isEmptyValue: isEmpty } = useChipState(label);

  const onChipDelete = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const handleDelete = useMemo(() => {
    return !isEmpty ? { handleDelete: onChipDelete } : null;
  }, [isEmpty, onChipDelete]);

  const value = useMemo(
    () =>
      label?.value
        ? {
            id: label.value.idValue ?? '',
            name: label.value.stringValue ?? ''
          }
        : undefined,
    [label]
  );

  return (
    <SearchableChip
      label={label}
      loading={loading}
      options={options}
      onOpen={onOpen}
      onSelect={onSelectValue}
      getOptionLabel={optionLabel}
      renderOption={defaultRenderOption}
      {...handleDelete}
      getOptionSelected={getOptionSelected}
      value={value}
      handleSearch={handleSearch}
      hasMore={hasMore}
      acceptSuggestion={acceptSuggestionHandler}
    />
  );
};

export default ActivityChip;
